"use client";
import React, { useEffect, useState } from "react";
import { IncrementButton, Slider } from "@template/components";
import * as S from "./styles.ts";
import { PlanTable, PlanTableProps } from "./PlanTable";
import PricingCard, {
  TPricingCard,
} from "@template/blocks/PricingSection/component/PricingCard";
import { useTypedSelector } from "@/redux/helpers/hooks.ts";
import { TAvailablePlan } from "@/redux/slices/store/types.ts";
import useCurrency from "@/lib/helpers/hooks/useCurrency.ts";
import CountriesPopover from "@template/blocks/ComparePricingSection/component/CountriesPopover";
import {
  AD,
  AT,
  AU,
  BE,
  BG,
  BR,
  CA,
  CH,
  CN,
  CY,
  CZ,
  DE,
  DK,
  EE,
  ES,
  FI,
  FR,
  GB,
  GI,
  GP,
  GR,
  GU,
  HK,
  HU,
  IE,
  IL,
  IN,
  IS,
  IT,
  JP,
  KR,
  LU,
  LV,
  MA,
  MN,
  MQ,
  MT,
  MX,
  MY,
  NL,
  NO,
  NZ,
  PE,
  PL,
  PR,
  PT,
  RO,
  RU,
  SE,
  SG,
  SK,
  TH,
  TW,
  US,
  VE,
} from "country-flag-icons/react/3x2";
import ArchStandaloneSectionComponent from "@template/blocks/ArchStandaloneSection/component";
import ToggleSwitch from "@/template/components/ToggleSwitch/index.tsx";
import { TBillingInterval } from "@/redux/slices/user/types.ts";

const getAdditionalPhoneNumberPrice = (userCount: number) => {
  if (userCount < 20) {
    return [
      "1 per account",
      `${userCount === 1 ? "1" : "2"} per account`,
      `${userCount} per account`,
    ];
  } else if (userCount < 100) {
    return ["5 per account", "10 per account", `${userCount} per account`];
  } else {
    return ["10 per account", "25 per account", `${userCount} per account`];
  }
};

const getFreePhoneNumbersAmount = (userCount: number) => {
  if (userCount < 2) {
    return 1.99;
  } else if (userCount < 20) {
    return 1.79;
  } else if (userCount < 50) {
    return 1.49;
  } else {
    return 1.29;
  }
};

const callHandlingData: PlanTableProps = {
  header: "Call Handling",
  rows: [
    { rowName: "HD Quality Calls", values: [true, true, true] },
    { rowName: "Call Encryption", values: [true, true, true] },
    { rowName: "Call Forwarding & Routing", values: [true, true, true] },
    {
      rowName: "Time of Day Routing",
      values: [true, "Unlimited", "Unlimited"],
    },
    {
      rowName: "Voicemail",
      values: ["Unlimited", "Unlimited", "Unlimited"],
    },
    {
      rowName: "Voicemail to Email",
      values: ["Unlimited", "Unlimited", "Unlimited"],
    },
    {
      rowName: "Music On Hold",
      values: ["Playlist", "Playlist", "Playlist"],
    },
    {
      rowName: "Ring Groups",
      values: [true, true, true],
    },
    {
      rowName: "Missed Call Alerts",
      values: [true, true, true],
    },
    { rowName: "Call Screening & Blocking", values: [true, true, true] },
    { rowName: "Call Assurance", values: [true, true, true] },
    {
      rowName: "SIP Trunks",
      values: ["Unlimited", "Unlimited", "Unlimited"],
    },
    {
      rowName: "Multi-Level IVR Call Menus",
      values: [false, true, true],
    },
    {
      rowName: "Voicemail Transcription",
      values: [false, true, true],
    },
    {
      rowName: "Caller ID Routing",
      values: [false, true, true],
    },
    { rowName: "Phone Book Manager", values: [false, true, true] },
    { rowName: "Dial By Extension", values: [false, true, true] },
    { rowName: "Call Queueing", values: [false, false, true] },
    { rowName: "Text To Speech", values: [false, false, true] },
  ],
};

const deviceData: PlanTableProps = {
  header: "Apps and Devices",
  rows: [
    { rowName: "iOS, Android Apps", values: [true, true, true] },
    { rowName: "Windows, Mac, Linux Apps", values: [true, true, true] },
    { rowName: "Browser Based Phone", values: [true, true, true] },
    { rowName: "VoIP Phone Compatible", values: [true, true, true] },
  ],
};

const callManagementData: PlanTableProps = {
  header: "Call Management",
  rows: [
    { rowName: "Call Logs", values: [true, true, true] },
    {
      rowName: "Call Recording",
      values: ["30 days", "30 days", "12 months"],
      tooltip: "Need longer storage? More options at checkout.",
    },
    {
      rowName: "Presence Indication (BLF)",
      values: [true, true, true],
    },
    { rowName: "Alternative Caller ID", values: [true, true, true] },
    {
      rowName: "Hold, Transfer and Park Functions",
      values: [true, true, true],
    },
    { rowName: "Call Monitoring", values: [false, true, true] },
  ],
};

const collaborationData: PlanTableProps = {
  header: "Collaboration",
  rows: [
    { rowName: "Conference Bridges", values: [true, true, true] },
    { rowName: "Hot Desking", values: [true, true, true] },
    {
      rowName: "Chat - Business Instant Messaging",
      values: [true, true, true],
    },
    {
      rowName: "SMS",
      values: [
        "Up to 10 recipients",
        "Up to 25 recipients",
        "Up to 2000 recipients",
      ],
    },
  ],
};

const analyticsData: PlanTableProps = {
  header: "Analytics",
  rows: [
    { rowName: "Call Feedback", values: [false, true, true] },
    { rowName: "Wallboard Statistics", values: [false, false, true] },
    {
      rowName: "Call Reporting (Inbound & Outbound)",
      values: [false, false, true],
    },
  ],
};

const workFlowEnhancementData: PlanTableProps = {
  header: "Workflow Enhancement",
  rows: [
    { rowName: "Webhooks", values: [true, true, true] },
    { rowName: "Short Code Commands", values: [true, true, true] },
    {
      rowName: "Application Programming Interface (API)",
      values: [true, true, true],
    },
    { rowName: "Fax To Email", values: [true, true, true] },
    { rowName: "Speed Dials", values: [true, true, true] },
    {
      rowName: "CRM and App Integration (Zoho, Hubspot, Saleforce, +more)",
      values: [false, true, true],
    },
    {
      rowName: "Click To Dial (Chrome, Firefox)",
      values: [false, true, true],
    },
    {
      rowName: "Phone Books (Office 365, Google Contacts)",
      values: [false, true, true],
    },
    { rowName: "Cloud Storage Sync", values: [false, false, true] },
  ],
};

const adminData: PlanTableProps = {
  header: "Admin",
  rows: [
    { rowName: "Roles & Permissions", values: [false, true, true] },
    { rowName: "Single Sign-On", values: [false, true, true] },
  ],
};

const aiCallFeaturesData: PlanTableProps = {
  header: "AI Call Features",
  rows: [
    { rowName: "AI Call Transcription", values: [false, false, true] },
    { rowName: "AI Call Summaries", values: [false, false, true] },
  ],
};

const tablesData: PlanTableProps[] = [
  callHandlingData,
  callManagementData,
  aiCallFeaturesData,
  deviceData,
  collaborationData,
  analyticsData,
  workFlowEnhancementData,
  adminData,
];

const generateFakePlan = (id: any) => {
  return {
    id,
    name: "loading",
    costs: {
      1: 0,
      2: 0,
      20: 0,
      100: 0,
    },
  };
};

const cards: Partial<TPricingCard>[] = [
  {
    copy: "Perfect for startups and those looking for virtual landline numbers with a Cloud service",
  },
  {
    copy: "Our most popular plan. Ideal for small offices, includes IVR call menus and more",
  },
  {
    copy: "Best for those needing unlimited minutes, international calls, and enterprise features",
  },
];

const ComparePricingSectionComponent = ({}) => {
  const [userCount, setUserCount] = useState(1);
  const [renewalPeriod, setRenewalPeriod] =
    useState<TBillingInterval>("yearly");
  const user = useTypedSelector((state) => state.user.user);

  const plans = useTypedSelector((state) => state.store.plan_prices);
  const currency = useCurrency();
  const plansLoaded = plans.loaded;
  const plansData = plans.plans;

  const fakePlans: TAvailablePlan[] = Array.from({ length: 3 }, (_, index) =>
    generateFakePlan(index + 1)
  );

  const [planData, setPlanData] = useState<PlanTableProps>({
    rows: [
      {
        rowName: "Free Minutes",
        values: [
          "100 per user",
          "750 per user",
          `Unlimited (${currency === "$" ? "US" : "UK"} + 54 Countries)`,
        ],
        popover: [
          <CountriesPopover
            groups={[
              {
                title: "Calls to Landline and Mobile",
                countries: [
                  {
                    title: "United States",
                    flag: <US title="United States Flag" />,
                  },
                  {
                    title: "Canada",
                    flag: <CA title="Canada Flag" />,
                  },
                  {
                    title: "United Kingdom",
                    flag: <GB title="United Kingdom Flag" />,
                  },
                ],
              },
            ]}
          />,
          <CountriesPopover
            groups={[
              {
                title: "Calls to Landline and Mobile",
                countries: [
                  {
                    title: "United States",
                    flag: <US title="United States Flag" />,
                  },
                  {
                    title: "Canada",
                    flag: <CA title="Canada Flag" />,
                  },
                  {
                    title: "United Kingdom",
                    flag: <GB title="United Kingdom Flag" />,
                  },
                ],
              },
            ]}
          />,
          <CountriesPopover
            groups={[
              {
                title: "Calls to Landline and Mobile",
                countries: [
                  {
                    title: "Australia",
                    flag: <AU title="Australia Flag" />,
                  },
                  {
                    title: "Austria",
                    flag: <AT title="Austria Flag" />,
                  },
                  {
                    title: "Belgium",
                    flag: <BE title="Belgium Flag" />,
                  },
                  {
                    title: "Canada",
                    flag: <CA title="Canada Flag" />,
                  },
                  {
                    title: "China",
                    flag: <CN title="China Flag" />,
                  },
                  {
                    title: "Denmark",
                    flag: <DK title="Denmark Flag" />,
                  },
                  {
                    title: "Finland",
                    flag: <FI title="Finland Flag" />,
                  },
                  {
                    title: "France",
                    flag: <FR title="France Flag" />,
                  },
                  {
                    title: "Germany",
                    flag: <DE title="Germany Flag" />,
                  },
                  {
                    title: "Guam",
                    flag: <GU title="Guam Flag" />,
                  },
                  {
                    title: "Hong Kong",
                    flag: <HK title="Hong Kong Flag" />,
                  },
                  {
                    title: "India",
                    flag: <IN title="India Flag" />,
                  },
                  {
                    title: "Ireland",
                    flag: <IE title="Ireland Flag" />,
                  },
                  {
                    title: "Israel",
                    flag: <IL title="Israel Flag" />,
                  },
                  {
                    title: "Italy",
                    flag: <IT title="Italy Flag" />,
                  },
                  {
                    title: "Luxembourg",
                    flag: <LU title="Luxembourg Flag" />,
                  },
                  {
                    title: "Malaysia",
                    flag: <MY title="Malaysia Flag" />,
                  },
                  {
                    title: "Malta",
                    flag: <MT title="Malta Flag" />,
                  },
                  {
                    title: "Mexico",
                    flag: <MX title="Mexico Flag" />,
                  },
                  {
                    title: "Mongolia",
                    flag: <MN title="Mongolia Flag" />,
                  },
                  {
                    title: "Netherlands",
                    flag: <NL title="Netherlands Flag" />,
                  },
                  {
                    title: "Norway",
                    flag: <NO title="Norway Flag" />,
                  },
                  {
                    title: "Peru",
                    flag: <PE title="Peru Flag" />,
                  },
                  {
                    title: "Poland",
                    flag: <PL title="Poland Flag" />,
                  },
                  {
                    title: "Portugal",
                    flag: <PT title="Portugal Flag" />,
                  },
                  {
                    title: "Puerto Rico",
                    flag: <PR title="Puerto Rico Flag" />,
                  },
                  {
                    title: "Romania",
                    flag: <RO title="Romania Flag" />,
                  },
                  {
                    title: "Singapore",
                    flag: <SG title="Singapore Flag" />,
                  },
                  {
                    title: "South Korea",
                    flag: <KR title="South Korea Flag" />,
                  },
                  {
                    title: "Spain",
                    flag: <ES title="Spain Flag" />,
                  },
                  {
                    title: "Sweden",
                    flag: <SE title="Sweden Flag" />,
                  },
                  {
                    title: "Thailand",
                    flag: <TH title="Thailand Flag" />,
                  },
                  {
                    title: "United Kingdom",
                    flag: <GB title="United Kingdom Flag" />,
                  },
                  {
                    title: "United States",
                    flag: <US title="United States Flag" />,
                  },
                ],
              },
              {
                title: "Calls to Landline Only",
                countries: [
                  {
                    title: "Andorra",
                    flag: <AD title="Andorra Flag" />,
                  },
                  {
                    title: "Brazil",
                    flag: <BR title="Brazil Flag" />,
                  },
                  {
                    title: "Bulgaria",
                    flag: <BG title="Bulgaria Flag" />,
                  },
                  {
                    title: "Cyprus",
                    flag: <CY title="Cyprus Flag" />,
                  },
                  {
                    title: "Czech Republic",
                    flag: <CZ title="Czech Republic Flag" />,
                  },
                  {
                    title: "Estonia",
                    flag: <EE title="Estonia Flag" />,
                  },
                  {
                    title: "Gibraltar",
                    flag: <GI title="Gibraltar Flag" />,
                  },
                  {
                    title: "Greece",
                    flag: <GR title="Greece Flag" />,
                  },
                  {
                    title: "Guadeloupe",
                    flag: <GP title="Guadeloupe Flag" />,
                  },
                  {
                    title: "Hungary",
                    flag: <HU title="Hungary Flag" />,
                  },
                  {
                    title: "Iceland",
                    flag: <IS title="Iceland Flag" />,
                  },
                  {
                    title: "Japan",
                    flag: <JP title="Japan Flag" />,
                  },
                  {
                    title: "Latvia",
                    flag: <LV title="Latvia Flag" />,
                  },
                  {
                    title: "Martinique",
                    flag: <MQ title="Martinique Flag" />,
                  },
                  {
                    title: "Morocco",
                    flag: <MA title="Morocco Flag" />,
                  },
                  {
                    title: "New Zealand",
                    flag: <NZ title="New Zealand Flag" />,
                  },
                  {
                    title: "Russian Federation",
                    flag: <RU title="Russian Federation Flag" />,
                  },
                  {
                    title: "Slovakia",
                    flag: <SK title="Slovakia Flag" />,
                  },
                  {
                    title: "Switzerland",
                    flag: <CH title="Switzerland Flag" />,
                  },
                  {
                    title: "Taiwan",
                    flag: <TW title="Taiwan Flag" />,
                  },
                  {
                    title: "Venezuela",
                    flag: <VE title="Venzuela Flag" />,
                  },
                ],
              },
            ]}
          />,
        ],
      },
      {
        rowName: "Free Phone Numbers",
        values: ["1 per account", "2 per account", "3 per account"],
      },
      {
        rowName: "Additional Phone Numbers",
        values: [`${currency}1.79`, `${currency}1.79`, `${currency}1.79`],
      },
      {
        rowName: "Inbound Calls",
        values: ["Unlimited", "Unlimited", "Unlimited"],
      },
    ],
  });

  const updateAdditionalNumberPrice = (newPrice) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      rows: prevPlanData.rows.map((row) =>
        row.rowName === "Additional Phone Numbers"
          ? {
              ...row,
              values: [
                `${currency}${newPrice}`,
                `${currency}${newPrice}`,
                `${currency}${newPrice}`,
              ],
            }
          : row
      ),
    }));
  };

  const updateFreePhoneNumbersAmount = (newArray) => {
    setPlanData((prevPlanData) => ({
      ...prevPlanData,
      rows: prevPlanData.rows.map((row) =>
        row.rowName === "Free Phone Numbers"
          ? { ...row, values: newArray }
          : row
      ),
    }));
  };

  const handleSetUserCount = (value: number) => {
    const min = 1;
    const max = 53;
    setUserCount(Math.min(Math.max(value, min), max));
  };

  useEffect(() => {
    updateAdditionalNumberPrice(getFreePhoneNumbersAmount(userCount));
    updateFreePhoneNumbersAmount(getAdditionalPhoneNumberPrice(userCount));
  }, [userCount]);

  const checkIfUserHasVoIP = user?.has_voip && user?.voip_plan !== 8;

  useEffect(() => {
    if (user?.has_voip) {
      setUserCount(user?.active_users);
    }
  }, [user?.has_voip]);

  return (
    <S.ComparePricingSectionWrapper>
      <S.ComparePricingSectionContainer>
        <S.SliderContainer>
          <S.Container>
            <S.LabelText>How many users?</S.LabelText>
            <IncrementButton
              value={userCount}
              setValue={handleSetUserCount}
              disabled={checkIfUserHasVoIP}
            />
          </S.Container>
          <Slider
            value={userCount}
            onChange={setUserCount}
            min={1}
            max={53}
            step={1}
            breaks={[20, 50]}
            showNumbers
            startText="1 User"
            showValue
            altStyling={true}
          />
          <ToggleSwitch
            isCompare={true}
            renewalPeriod={renewalPeriod}
            setRenewalPeriod={setRenewalPeriod}
          />
        </S.SliderContainer>
        <S.PricingCardsContainer>
          <ArchStandaloneSectionComponent
            color="white"
            background="dark"
            withPadding={true}
            style={{
              position: "absolute",
              top: 250,
              left: 0,
            }}
          />
          {(plansLoaded ? plansData : fakePlans).map((plan, index) => (
            <PricingCard
              key={plan.id}
              type={plan.id - 1}
              copy={cards[index]?.copy}
              userCount={userCount}
              button={
                index === 1
                  ? {}
                  : {
                      variant: "outline",
                    }
              }
              loading={!plansLoaded}
              renewalPeriod={renewalPeriod}
            />
          ))}
          <S.Paragraph>
            Fully Supported Enterprise Cloud PBX. No Contract, Pay Monthly.
            Prices ex. VAT.
          </S.Paragraph>
        </S.PricingCardsContainer>
        {/* Mobile-only sticky plan names — rendered once, above the first table */}
        <S.PlanNamesRow>
          <span />
          <span>Core</span>
          <span>Standard</span>
          <span>Premium</span>
        </S.PlanNamesRow>
        <PlanTable {...planData} />
        {tablesData.map((table, index) => (
          <PlanTable
            key={index}
            {...table}
          />
        ))}
      </S.ComparePricingSectionContainer>
    </S.ComparePricingSectionWrapper>
  );
};

export default ComparePricingSectionComponent;
export const options = [
  {
    description: "",
    displayName: "ComparePricingSectionComponent",
    methods: [],
  },
];
